// A4 — the "get out of the chair" pill: the inverse of storyDirector's "sit and
// listen". It surfaces only while seated (the LockedFreeform chair camera is
// active) and, on click, tweens the camera back onto the interior rail. Glass
// pill matching the sit button so the two read as one affordance grammar (a
// button comes up rather than an instant jump, so leaving is a deliberate beat).
//
// DEMO-only; ticked from the render loop (write-on-change show/hide).

export interface GetOutPillDeps {
  /** True while seated — the pill shows only then (LockedFreeform is active). */
  isSeated: () => boolean;
  /** Leave the chair → tween back to the rail. */
  onGetOut: () => void;
  /** Pill copy — John's eye (the inverse of "sit and listen"). */
  label?: string;
}

export interface GetOutPill {
  /** Poll `isSeated()` and show/hide accordingly (write-on-change). */
  update(): void;
  dispose(): void;
}

export function createGetOutPill(deps: GetOutPillDeps): GetOutPill {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = deps.label ?? 'stand up';
  btn.style.cssText = [
    // Item 1 — same ACTION SLOT above the story card as the "sit and listen" pill
    // (they never co-show), so the contextual action always appears in one place.
    'position: fixed', 'bottom: 33%', 'left: 50%', 'transform: translate(-50%, 8px)',
    // Legibility pass (John, 2026-06-29) — bright text on darker glass, larger + bold.
    'padding: 13px 30px', 'background: rgba(20,26,38,0.68)', 'color: #f4f8ff',
    'border: 1px solid rgba(150,178,228,0.45)', 'border-radius: 14px',
    'font: 700 16px/1.2 system-ui, sans-serif', 'letter-spacing: 0.02em',
    'text-shadow: 0 1px 3px rgba(0,0,0,0.55)', 'cursor: pointer', 'user-select: none',
    'backdrop-filter: blur(6px)', '-webkit-backdrop-filter: blur(6px)',
    'box-shadow: 0 3px 14px rgba(0,0,0,0.34)',
    'z-index: 1180', 'opacity: 0', 'pointer-events: none',
    'transition: opacity 320ms ease, transform 320ms ease',
  ].join('; ');
  document.body.appendChild(btn);

  let shown = false;
  const show = (): void => {
    if (shown) return;
    shown = true;
    btn.style.opacity = '1';
    btn.style.pointerEvents = 'auto';
    btn.style.transform = 'translate(-50%, 0)';
  };
  const hide = (): void => {
    if (!shown) return;
    shown = false;
    btn.style.opacity = '0';
    btn.style.pointerEvents = 'none';
    btn.style.transform = 'translate(-50%, 8px)';
  };
  btn.addEventListener('click', () => { hide(); deps.onGetOut(); });

  const update = (): void => { if (deps.isSeated()) show(); else hide(); };
  update();

  return { update, dispose(): void { btn.remove(); } };
}
